// Renders a trusted authored string (frontmatter) with a small subset of inline
// markdown: `**bold**` emphasis and `[label](https://…)` links, including bold
// inside a link label. Links accept only http(s) URLs, so there is no
// scheme-injection surface. Bold inside a link inherits the link's accent color
// (weight only); standalone bold darkens to text-ink to stand out from muted
// body text.
import * as React from 'react';
import type { ReactNode } from 'react';

const LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const BOLD_PATTERN = /\*\*([^*]+)\*\*/g;

function pushBold(nodes: ReactNode[], text: string, inLink: boolean, keyState: { k: number }) {
  const strongClass = inLink ? 'font-medium' : 'font-medium text-ink';
  let lastIndex = 0;
  for (const match of text.matchAll(BOLD_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));
    nodes.push(
      <strong key={`s${keyState.k++}`} className={strongClass}>
        {match[1]}
      </strong>,
    );
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
}

export function renderInline(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  const keyState = { k: 0 };
  let lastIndex = 0;
  for (const match of text.matchAll(LINK_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) pushBold(nodes, text.slice(lastIndex, index), false, keyState);
    const [full, label, href] = match;
    const labelNodes: ReactNode[] = [];
    pushBold(labelNodes, label, true, keyState);
    nodes.push(
      <a
        key={`a${keyState.k++}`}
        href={href}
        className="text-accent underline decoration-accent/30 underline-offset-4 transition hover:decoration-accent"
      >
        {labelNodes}
      </a>,
    );
    lastIndex = index + full.length;
  }
  if (lastIndex < text.length) pushBold(nodes, text.slice(lastIndex), false, keyState);
  return nodes.length ? nodes : text;
}
